import React, { useState } from "react";
import { staffAPI } from "../../services/api";
import "./CreateAccount.css";

/**
 * Component for staff to create new accounts
 */
const ACCOUNT_TYPE_OPTIONS = [
  {
    value: "member",
    label: "Member / Customer",
    helper:
      "Creates a customer account with Stripe setup so they can purchase and book normally.",
  },
  {
    value: "staff",
    label: "Staff",
    helper: "Creates an internal staff login for the staff dashboard.",
  },
  {
    value: "massage_therapist",
    label: "Massage Therapist",
    helper:
      "Creates a therapist login that can be linked to a massage provider profile.",
  },
  {
    value: "luminary",
    label: "Luminary",
    helper:
      "Creates a yoga instructor account using the Luminary role and dashboard.",
  },
];

const CreateAccount = ({
  onSuccess,
  onCancel,
  title = "Create New Account",
  successTitle = "Account Created Successfully!",
  showCancelButton = true,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    contactNumber: "",
    role: "member",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [createdAccount, setCreatedAccount] = useState(null);

  const selectedAccountType =
    ACCOUNT_TYPE_OPTIONS.find((option) => option.value === formData.role) ||
    ACCOUNT_TYPE_OPTIONS[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validate form data
    if (!formData.firstName.trim()) {
      setError("First name is required");
      return;
    }

    if (!formData.lastName.trim()) {
      setError("Last name is required");
      return;
    }

    if (!formData.email.trim() || !validateEmail(formData.email)) {
      setError("Valid email address is required");
      return;
    }

    try {
      setLoading(true);
      const response = await staffAPI.createAccount(formData);
      setSuccess(true);
      setCreatedAccount(response.data.account);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(response.data.account);
      }
    } catch (err) {
      console.error("Error creating account:", err);

      // If user already exists, show a more specific error
      if (err.response && err.response.status === 409) {
        setError(`${err.response.data.message} Please use a different email.`);
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to create account. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleCreateAnother = () => {
    setFormData({
      firstName: "",
      lastName: "",
      preferredName: "",
      email: "",
      contactNumber: "",
      role: "member",
    });
    setSuccess(false);
    setCreatedAccount(null);
    setError("");
  };

  return (
    <div className="create-account-container">
      <h2>{title}</h2>

      {success ? (
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h3>{successTitle}</h3>
          <p>
            An account setup email has been sent to{" "}
            <strong>{createdAccount.email}</strong> so they can set their password
            and sign in.
          </p>
          <div className="account-details">
            <p>
              <strong>Name:</strong> {createdAccount.firstName}{" "}
              {createdAccount.lastName}
            </p>
            <p>
              <strong>Email:</strong> {createdAccount.email}
            </p>
            <p>
              <strong>Account Type:</strong>{" "}
              {ACCOUNT_TYPE_OPTIONS.find(
                (option) => option.value === createdAccount.role
              )?.label || createdAccount.role}
            </p>
          </div>
          <div className="account-button-group">
            <button
              className="create-account-primary-btn"
              onClick={handleCreateAnother}
            >
              Create Another Account
            </button>
            {showCancelButton && (
              <button
                className="create-account-secondary-btn"
                onClick={handleCancel}
              >
                Done
              </button>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="create-account-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="role">Account Type</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              {ACCOUNT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="form-helper-text">{selectedAccountType.helper}</p>
          </div>

          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="preferredName">Preferred Name</label>
            <input
              type="text"
              id="preferredName"
              name="preferredName"
              value={formData.preferredName}
              onChange={handleChange}
              placeholder="Optional preferred name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactNumber">Contact Number</label>
            <input
              type="tel"
              id="contactNumber"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder="Optional phone number"
            />
          </div>

          <div className="form-note">
            <p>
              <strong>Note:</strong> We’ll create the account and email them a secure
              password setup link. Member accounts will also get a Stripe customer
              record so they’re ready for purchases and booking.
            </p>
          </div>

          <div className="account-button-group">
            <button
              type="submit"
              className="create-account-primary-btn"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create"}
            </button>
            {showCancelButton && (
              <button
                type="button"
                className="create-account-secondary-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateAccount;
